import { Plus, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Banner } from '@/components/feedback/Banner'
import { EmptyState } from '@/components/feedback/EmptyState'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Checkbox } from '@/components/ui/Checkbox'
import { Input } from '@/components/ui/Input'
import { useSession } from '@/features/auth/api/useSession'
import {
  useCounterparties,
  useCreateCounterparty,
} from '@/features/counterparties/api/useCounterparties'
import { useCounterpartiesMoney } from '@/features/counterparties/api/useCounterpartyMoney'
import { CounterpartiesTable } from '@/features/counterparties/components/CounterpartiesTable'
import { CounterpartyCategoryChips } from '@/features/counterparties/components/CounterpartyCategoryChips'
import {
  CounterpartyFormSheet,
  type CounterpartyDraft,
} from '@/features/counterparties/components/CounterpartyFormSheet'
import {
  categoryCounts,
  COUNTERPARTY_CATEGORIES,
  counterpartyRights,
  isCounterpartyCategory,
  visibleCounterparties,
} from '@/features/counterparties/counterpartyList'
import { counterpartySaveErrorMessage } from '@/features/directories/directoryText'
import type { CounterpartyCategory } from '@/types/domain'

/* «Контрагенты» (S-22b, `list-a-chips.png`): every company or person that
 * provides us a service, by category.
 *
 * The page reads EVERY row once (`includeInactive`) and narrows on the client
 * — the chips' counts need the whole list, and a list of a company's
 * counterparties is not a public register. The chip and the box live in the
 * URL, so the way back from a record page lands on the same narrowing; the
 * search is the reader's typing and stays local.
 *
 * The only blue fill is «Новый контрагент», offered to a holder of
 * `counterparty.create` (with the read the server asks beside it). A reader
 * without `counterparty.read` is told the section is not theirs rather than
 * shown an empty list that would read as "the company has no carriers".
 *
 * The money columns (S-22e) come from a second read, the list money, merged by
 * id in the table: all-time figures over the reader's case-money reach, with
 * «Цифры по вашим работам» above the table when that reach is not the
 * company's. A failed money read is said above the table and the directory
 * stays whole; a forbidden one shows no money columns at all. */
export function CounterpartiesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: session, isPending: sessionPending } = useSession()
  const rights = counterpartyRights(session)

  const { data, isPending, isError, error, refetch } = useCounterparties(
    { includeInactive: true },
    { enabled: rights.read },
  )
  const money = useCounterpartiesMoney({ enabled: rights.read })
  const create = useCreateCounterparty()
  const [creating, setCreating] = useState(false)
  const [search, setSearch] = useState('')

  const requested = searchParams.get('category') ?? ''
  const category: CounterpartyCategory | 'all' = isCounterpartyCategory(requested)
    ? requested
    : 'all'
  const inactive = searchParams.get('inactive') === '1'

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams)
    if (value === null) next.delete(key)
    else next.set(key, value)
    setSearchParams(next, { replace: true })
  }

  const all = useMemo(() => data ?? [], [data])
  const rows = useMemo(
    () => visibleCounterparties(all, { category, search, inactive }),
    [all, category, search, inactive],
  )
  const counts = useMemo(() => categoryCounts(all, inactive), [all, inactive])

  const clearFilters = () => {
    setSearch('')
    const next = new URLSearchParams(searchParams)
    next.delete('category')
    next.delete('inactive')
    setSearchParams(next, { replace: true })
  }

  const openCreate = () => {
    create.reset()
    setCreating(true)
  }

  const submit = (values: CounterpartyDraft & { category: CounterpartyCategory }) => {
    create.mutate(
      {
        name: values.name.trim(),
        category: values.category,
        taxId: values.taxId.trim(),
        registrationId: values.registrationId.trim(),
        country: values.country.trim(),
        address: values.address.trim(),
        contacts: values.contacts,
      },
      { onSuccess: () => setCreating(false) },
    )
  }

  const header = (
    <PageHeader
      title={t('counterparties.title')}
      subtitle={t('counterparties.subtitle')}
      actions={
        rights.edit ? (
          <Button size="sm" iconLeft={Plus} onClick={openCreate}>
            {t('counterparties.new')}
          </Button>
        ) : undefined
      }
    />
  )

  if (!sessionPending && !rights.read) {
    return (
      <>
        <PageHeader title={t('counterparties.title')} />
        <Card>
          <EmptyState
            title={t('counterparties.forbidden.title')}
            description={t('counterparties.forbidden.description')}
          />
        </Card>
      </>
    )
  }

  return (
    <>
      {header}

      {/* The chips, the search and the box on ONE row (Locked visual
          decision); it wraps on a narrow screen instead of scrolling. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2.5">
        <CounterpartyCategoryChips
          value={category}
          counts={counts}
          onChange={(next) => setParam('category', next === 'all' ? null : next)}
        />
        <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-x-4 gap-y-2.5">
          <Input
            iconLeft={Search}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onClear={() => setSearch('')}
            placeholder={t('counterparties.search')}
            aria-label={t('counterparties.search')}
            className="w-full sm:w-64"
          />
          <Checkbox
            label={t('counterparties.inactive')}
            checked={inactive}
            onCheckedChange={(checked) => setParam('inactive', checked ? '1' : null)}
          />
        </div>
      </div>

      {money.data?.moneyUnavailable === 'unavailable' ? (
        <Banner tone="attention" title={t('counterparties.money.unavailable.title')}>
          {t('counterparties.money.unavailable.listBody')}
        </Banner>
      ) : null}

      <Card>
        {money.data?.scoped && money.data.moneyUnavailable === undefined ? (
          <p className="text-text-muted border-line-hair border-b px-[18px] py-2.5 text-[12.5px] font-light">
            {t('counterparties.money.scoped')}
          </p>
        ) : null}
        {isError ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : isPending ? (
          <Skeleton className="h-64 rounded-[var(--radius-DEFAULT)]" />
        ) : (
          <CounterpartiesTable
            rows={rows}
            filtered={rows.length === 0 && all.length > 0}
            onClearFilters={clearFilters}
            onOpen={(row) => navigate(`/counterparties/${row.id}`)}
            money={money.data}
          />
        )}
      </Card>

      {rights.edit ? (
        <CounterpartyFormSheet
          open={creating}
          onClose={() => setCreating(false)}
          categories={COUNTERPARTY_CATEGORIES}
          pending={create.isPending}
          error={create.error ? counterpartySaveErrorMessage(create.error, t) : undefined}
          onSubmit={submit}
        />
      ) : null}
    </>
  )
}
